import React from 'react';
import PropTypes from 'prop-types';
import { ChevronRight } from 'lucide-react';
import AvatarStack from '../ui/AvatarStack.jsx';

/**
 * Eine Zeile im Skill-Katalog (Skills entdecken, PLAN § 2.1).
 *
 *   Excel                                   (A)(B)(C)  ›
 *
 * Bewusst nur Name und Köpfe (Heiko, 18.10.2026): Zahlen und farbige Punkte
 * je Zeile machten die Übersicht unruhig und brachten Grün in die Liste
 * (CorporateDesign v2.0: Grün nur in der Skill-Auswahl). Wie viele helfen
 * oder lernen wollen, zeigt das Detailblatt in seinen Reitern. Screenreader
 * bekommen die Zahlen weiter über `describeSkill`.
 */

export const helpLabel = n => `${n} ${n === 1 ? 'kann' : 'können'} helfen`;
export const learnLabel = n => `${n} ${n === 1 ? 'will' : 'wollen'} lernen`;
export const meLabel = me => (me === 'offer' ? 'Du kannst helfen' : me === 'seek' ? 'Du willst das lernen' : '');

/** Beschriftung für Screenreader: Name und alle Zahlen in einem Satz. */
export const describeSkill = (skill) => {
  const parts = [];
  if (skill.offerCount > 0) parts.push(helpLabel(skill.offerCount));
  if (skill.seekCount > 0) parts.push(learnLabel(skill.seekCount));
  const me = meLabel(skill.me);
  return `${skill.name}${parts.length ? `: ${parts.join(', ')}` : ''}${me ? `. ${me}` : ''}`;
};

export const toStackPeople = (preview = []) => preview.map(person => ({
  id: person.userId,
  name: person.displayName,
  src: person.avatar || undefined,
}));

/** Bis zu drei Köpfe der Helfenden, ohne Zahl daneben. */
export const HelperStack = ({ preview = [], className = '' }) => {
  const people = toStackPeople(preview).slice(0, 3);
  if (people.length === 0) return null;
  return (
    <span className={`inline-flex shrink-0 items-center ${className}`} aria-hidden="true">
      <AvatarStack people={people} size="xs" max={3} animateNew={false} decorative />
    </span>
  );
};

HelperStack.propTypes = {
  preview: PropTypes.array,
  className: PropTypes.string,
};

const SkillBrowseRow = ({ skill, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(skill)}
    aria-label={describeSkill(skill)}
    className={[
      'flex w-full min-h-[56px] items-center gap-3 px-3 py-2.5 text-left touch-manipulation',
      'transition-colors duration-fast hover:bg-foreground/[0.03]',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary',
    ].join(' ')}
  >
    <span className="min-w-0 flex-1 truncate text-title-s font-semibold text-foreground" title={skill.name}>
      {skill.name}
    </span>
    {skill.offerCount > 0 && <HelperStack preview={skill.preview} />}
    <ChevronRight className="h-4 w-4 shrink-0 text-faint" aria-hidden="true" />
  </button>
);

export const skillShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  offerCount: PropTypes.number,
  seekCount: PropTypes.number,
  me: PropTypes.oneOf(['offer', 'seek', null]),
  preview: PropTypes.arrayOf(PropTypes.shape({
    userId: PropTypes.string,
    displayName: PropTypes.string,
    avatar: PropTypes.string,
  })),
});

SkillBrowseRow.propTypes = {
  skill: skillShape.isRequired,
  onOpen: PropTypes.func.isRequired,
};

export default SkillBrowseRow;
